/**
 * Applying the configuration to the GUI: colours and fonts (as CSS variables), the window
 * layout, the defaults of the labels switch and of the spacing. Keyboard shortcuts are applied in `keys.ts`.
 *
 * The conversions are pure functions (tested in `configApply.test.ts`); {@link installConfig}
 * wires them to the config mirror so a `config.changed` event takes effect immediately.
 */
import { createEffect, on } from "solid-js";
import type { Config } from "@protocol/Config";
import type { LayoutArea } from "@protocol/LayoutArea";
import type { LayoutConfig } from "@protocol/LayoutConfig";
import type { ThemeConfig } from "@protocol/ThemeConfig";
import { load, save } from "../shared/storage";
import { split, tabs, type Area, type Layout } from "../wm/layout";
import type { App } from "./context";

/** Palette key of the config → CSS variable of `styles.css`. */
export const PALETTE_VARIABLES: Record<string, string> = {
  bg: "--bg",
  panel: "--panel",
  panel2: "--panel2",
  line: "--line",
  canvas: "--canvas",
  fg: "--fg",
  muted: "--muted",
  accent: "--accent",
  "accent-soft": "--accent-soft",
  danger: "--danger",
  ok: "--ok",
  ...Object.fromEntries(Array.from({ length: 8 }, (_, i) => [`category-${i + 1}`, `--category-${i + 1}`])),
  "category-none": "--category-none",
  inherit: "--e-inherit",
  depend: "--e-depend",
  use: "--e-use",
};

/** Turns a palette map into CSS declarations (`--accent: #fff; ...`). Unknown keys are skipped. */
function declarations(colours: Record<string, string | undefined>): string {
  return Object.entries(colours)
    .flatMap(([key, value]) => (PALETTE_VARIABLES[key] && value ? [`${PALETTE_VARIABLES[key]}: ${value};`] : []))
    .join(" ");
}

/**
 * The CSS that applies a theme config. Light colours go on `:root`; dark ones use the same
 * selectors as `styles.css`, so this sheet (added after it) wins in both modes.
 */
export function themeCss(theme: ThemeConfig): string {
  const base: string[] = [];
  if (theme.font_ui) base.push(`--font-ui: "${theme.font_ui.replaceAll('"', "")}", system-ui, sans-serif;`);
  if (theme.font_mono) base.push(`--font-mono: "${theme.font_mono.replaceAll('"', "")}", ui-monospace, monospace;`);
  const light = declarations(theme.light);
  const dark = declarations(theme.dark);
  return [
    `:root { ${base.join(" ")} ${light} }`,
    dark ? `@media (prefers-color-scheme: dark) { :root:not([data-theme="light"]) { ${dark} } }` : "",
    dark ? `:root[data-theme="dark"] { ${dark} }` : "",
  ]
    .filter(Boolean)
    .join("\n");
}

/** Converts one area of the config layout. */
function area(config: LayoutArea): Area {
  if (config.kind === "tabs") return tabs([...config.windows], config.active ?? config.windows[0] ?? "");
  const children = config.children.map(area);
  const given = config.children.map((c) => c.size);
  const used = given.reduce<number>((sum, s) => sum + (s ?? 0), 0);
  const unset = given.filter((s) => s == null).length;
  const share = unset ? Math.max(0, 1 - used) / unset : 0;
  return split(config.direction, children, given.map((s) => s ?? share));
}

/** Converts the config's layout into the window manager's layout. */
export function layoutFromConfig(config: LayoutConfig): Layout {
  return {
    root: config.root ? area(config.root) : null,
    floating: config.floating.map((f) => ({ window: f.window, x: f.x, y: f.y, width: f.width, height: f.height })),
    closed: [...config.closed],
  };
}

/** The config layout last applied in this browser (so it is applied once per change). */
const APPLIED_LAYOUT_KEY = "developer-app-config-layout-v1";

/**
 * Applies the configuration whenever it changes:
 * - colours and fonts, at once;
 * - the theme mode and the labels default, when the config changes them;
 * - the layout, when the config's layout differs from the last one applied in this browser
 *   (so your own window moves are kept until the config's layout is edited).
 */
export function installConfig(app: App): void {
  const { theme } = app;
  const style = document.createElement("style");
  style.id = "config-theme";
  document.head.append(style);

  createEffect(
    on(
      () => app.config.config(),
      (config: Config | null, previous: Config | null | undefined) => {
        if (!config) return;
        style.textContent = themeCss(config.theme);
        // Lets the graph canvas know the colours changed.
        document.documentElement.dataset.configRevision = String(app.config.state()?.revision ?? 0);
        if (!previous || previous.theme.mode !== config.theme.mode) theme.set(config.theme.mode);
        if (!previous || previous.graph.labels !== config.graph.labels) app.view.setLabels(config.graph.labels);
        // Only when the config's value changes: the slider's own choice survives a reload.
        if (previous && previous.graph.spacing !== config.graph.spacing) app.view.setSpacing(config.graph.spacing);

        const layoutKey = JSON.stringify(config.layout);
        if (load<string | null>(APPLIED_LAYOUT_KEY, null) !== layoutKey) {
          save(APPLIED_LAYOUT_KEY, layoutKey);
          app.wm.reset();
        }
      },
    ),
  );
}
